/** <scapescene-player src="..." subtitles="..."> — a thin wrapper
 * around <video> that only swaps the underlying source when the "src"
 * attribute actually changes, so background polling never interrupts
 * playback. "subtitles" is a WebVTT URL shown as an always-on track
 * (the narration is no longer burned into the picture). */

import { assetKey } from "../data/client";
import { getLang, t } from "../lib/i18n";

class WorkshopVideoPlayer extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["src", "subtitles"];
  }

  #video: HTMLVideoElement | null = null;
  #track: HTMLTrackElement | null = null;

  connectedCallback(): void {
    if (!this.#video) {
      this.innerHTML = "";
      const video = document.createElement("video");
      video.className = "video-player";
      video.controls = true;
      video.playsInline = true;
      video.preload = "metadata";
      this.appendChild(video);
      this.#video = video;
    }
    this.#syncSrc();
    this.#syncSubtitles();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (name === "src") this.#syncSrc();
    if (name === "subtitles") this.#syncSubtitles();
  }

  #syncSubtitles(): void {
    if (!this.#video) return;
    const url = this.getAttribute("subtitles") ?? "";
    if (!url) {
      this.#track?.remove();
      this.#track = null;
      return;
    }
    if (!this.#track) {
      const track = document.createElement("track");
      track.kind = "subtitles";
      track.srclang = getLang();
      track.label = t("subtitles");
      track.default = true;
      this.#video.appendChild(track);
      this.#track = track;
    }
    if (this.#track.getAttribute("src") !== url) {
      this.#track.setAttribute("src", url);
    }
    // "default" is only honoured at load; force the track on so the
    // subtitles are visible in every player, every time.
    this.#track.track.mode = "showing";
  }

  #syncSrc(): void {
    if (!this.#video) return;
    const src = this.getAttribute("src") ?? "";
    // assetKey, never the raw URL: a presigned bucket URL carries a new
    // signature on every poll while pointing at the same file — swapping
    // it in would restart the film every few seconds.
    const current = this.#video.getAttribute("src") ?? "";
    if (assetKey(current) !== assetKey(src)) {
      if (src) {
        this.#video.setAttribute("src", src);
      } else {
        this.#video.removeAttribute("src");
      }
    }
  }
}

if (!customElements.get("scapescene-player")) {
  customElements.define("scapescene-player", WorkshopVideoPlayer);
}

export {};
